import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminCoupon, CouponType } from '../../hooks/useAdminCoupons'

// ── Helpers ────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

// ── Styles ─────────────────────────────────────────────

const styles = {
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  } as React.CSSProperties,
  badge: (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  }),
  btnSecondary: {
    padding: '0.35rem 0.65rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
  } as React.CSSProperties,
  btnDanger: {
    padding: '0.35rem 0.65rem',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
  } as React.CSSProperties,
  btnToggle: (active: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.65rem',
    background: active ? '#fef2f2' : '#f0fdf4',
    color: active ? '#dc2626' : '#16a34a',
    border: `1px solid ${active ? '#fecaca' : '#bbf7d0'}`,
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
  }),
  searchRow: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  } as React.CSSProperties,
  searchInput: {
    padding: '0.45rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    minWidth: '200px',
  } as React.CSSProperties,
  filterSelect: {
    padding: '0.45rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    background: 'white',
  } as React.CSSProperties,
  confirmOverlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1100,
  } as React.CSSProperties,
  confirmBox: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '1.5rem 2rem',
    maxWidth: '400px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
  } as React.CSSProperties,
}

// ── Component ──────────────────────────────────────────

type CouponsTableProps = {
  coupons: AdminCoupon[]
  loading: boolean
  onEdit: (coupon: AdminCoupon) => void
  onToggle: (couponId: string, active: boolean) => Promise<boolean>
  onDelete: (couponId: string) => Promise<boolean>
  couponType?: CouponType
}

const CouponsTable: React.FC<CouponsTableProps> = ({
  coupons,
  loading,
  onEdit,
  onToggle,
  onDelete,
  couponType = 'PARTNER',
}) => {
  const navigate = useNavigate()

  // ── Local state ──
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // ── Filtering ──
  const filtered = coupons.filter((c) => {
    if (search && !c.code.toLowerCase().includes(search.toLowerCase())) return false
    if (statusFilter === 'active' && !c.is_active) return false
    if (statusFilter === 'disabled' && c.is_active) return false
    return true
  })

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return
    await onDelete(confirmDeleteId)
    setConfirmDeleteId(null)
  }

  return (
    <>
      {/* Search & Filters */}
      <div style={styles.searchRow}>
        <input
          style={styles.searchInput}
          data-testid="coupon-search"
          type="text"
          placeholder="Rechercher par code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={styles.filterSelect}
          data-testid="coupon-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actifs</option>
          <option value="disabled">Désactivés</option>
        </select>
      </div>

      {loading && <p style={{ color: '#64748b' }}>Chargement…</p>}

      {!loading && filtered.length === 0 && (
        <p style={{ color: '#94a3b8', fontStyle: 'italic' }} data-testid="no-coupons-msg">
          Aucun coupon trouvé.
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} data-testid="coupons-table">
            <thead>
              <tr>
                <th style={styles.th}>Code</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Statut</th>
                <th style={styles.th}>Début</th>
                <th style={styles.th}>Fin</th>
                <th style={styles.th}>Commission %</th>
                <th style={styles.th}>
                  {couponType === 'USER' ? 'Réduction user %' : 'Réduction membre %'}
                </th>
                <th style={styles.th}>Utilisations</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`coupon-row-${c.id}`}>
                  <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 600 }}>
                    {c.code}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      background: c.type === 'PARTNER' ? '#eff6ff' : '#fdf4ff',
                      color: c.type === 'PARTNER' ? '#1d4ed8' : '#7c3aed',
                    }}>
                      {c.type}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.badge(c.is_active)}>
                      {c.is_active ? 'Actif' : 'Désactivé'}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(c.valid_from)}</td>
                  <td style={styles.td}>{formatDate(c.valid_until)}</td>
                  <td style={styles.td}>{c.partner_commission_pct}%</td>
                  <td style={styles.td}>
                    {c.type === 'USER' ? `${c.user_discount_pct ?? 0}%` : `${c.member_discount_pct}%`}
                  </td>
                  <td style={styles.td}>
                    {c.current_usage_count}
                    {c.max_usage_count !== null && ` / ${c.max_usage_count}`}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button
                        style={styles.btnSecondary}
                        onClick={() => navigate(`/admin/coupons/${c.id}/view`)}
                        title="Voir coupon"
                        data-testid={`view-coupon-${c.id}`}
                      >
                        👁
                      </button>
                      <button
                        style={styles.btnSecondary}
                        onClick={() => onEdit(c)}
                        title="Modifier"
                        data-testid={`edit-coupon-${c.id}`}
                      >
                        ✏️
                      </button>
                      <button
                        style={styles.btnToggle(c.is_active)}
                        onClick={() => onToggle(c.id, c.is_active)}
                        title={c.is_active ? 'Désactiver' : 'Activer'}
                        data-testid={`toggle-coupon-${c.id}`}
                      >
                        {c.is_active ? '⏸' : '▶️'}
                      </button>
                      <button
                        style={styles.btnDanger}
                        onClick={() => setConfirmDeleteId(c.id)}
                        title="Supprimer"
                        data-testid={`delete-coupon-${c.id}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation de suppression */}
      {confirmDeleteId && (
        <div style={styles.confirmOverlay} data-testid="delete-confirm-dialog">
          <div style={styles.confirmBox}>
            <h3 style={{ marginTop: 0, color: '#1e293b' }}>Confirmer la suppression</h3>
            <p style={{ color: '#475569', fontSize: '0.9rem' }}>
              Êtes-vous sûr de vouloir supprimer ce coupon ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                style={styles.btnSecondary}
                onClick={() => setConfirmDeleteId(null)}
                data-testid="cancel-delete-btn"
              >
                Annuler
              </button>
              <button
                style={styles.btnDanger}
                onClick={handleDeleteConfirm}
                data-testid="confirm-delete-btn"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CouponsTable
